import { useEffect, useRef, type RefObject } from 'react';
import { fg } from '@atlassian/jira-feature-gating';

export const useKeyboardNavigation = (ref: RefObject<HTMLElement | null>) => {
	const lastFocusedElement = useRef<HTMLElement | null | undefined>();

	useEffect(() => {
		const table = ref.current;
		if (!table) return;
		if (!fg('nin-grid-keyboard-navigation')) return;

		const getCell = (ele: Element | null | undefined) => ele?.closest('td') ?? ele?.closest('th');
		const getFocusableElements = () =>
			Array.from(
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				document.querySelectorAll(
					[
						'a[href]:not([tabindex="-1"]):not([href=""]):not([href="javascript:;"])',
						'button:not([tabindex="-1"])',
						'input:not([tabindex="-1"], [type="hidden"])',
						'select:not([tabindex="-1"])',
						'textarea:not([tabindex="-1"])',
						"[tabindex]:not([tabindex='-1'])",
					].join(','),
				),
			);
		const onKeyDown = (event: KeyboardEvent) => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const activeElement = document.activeElement;
			const currentTr = activeElement?.closest('tr');

			if (event.key === 'Escape') {
				event.preventDefault();
				getCell(activeElement)?.focus();
				return;
			}

			if (event.key === 'Tab' && event.shiftKey && activeElement instanceof Element) {
				/*
					If focus would move from our cell to another cell when pressing shift+tab;
					Prevent default and focus first cell in the table.
				*/
				const isCell = getCell(activeElement) === activeElement;
				const index = activeElement.getAttribute('tabindex');
				if (isCell) activeElement.setAttribute('tabindex', '0');
				const focusable = getFocusableElements();
				const currentFocusableElementsCell = getCell(activeElement);
				const nextFocusableElementsCell = getCell(focusable[focusable.indexOf(activeElement) - 1]);
				if (isCell) activeElement.setAttribute('tabindex', index ?? '-1');
				if (
					currentFocusableElementsCell === nextFocusableElementsCell ||
					!nextFocusableElementsCell
				) {
					return;
				}
				const firstElementInsideTable = focusable.find((ele): ele is HTMLElement =>
					table.contains(ele),
				);
				event.preventDefault();
				firstElementInsideTable?.focus();
				return;
			}

			if (event.key === 'Tab' && activeElement) {
				/*
					If focus would move from our cell to another cell when pressing tab;
					Prevent default and focus first interactable thing outside the table. (probably create button but not always)
					If we're inside a dialog; don't programmatically control focus.
				*/
				if (activeElement.closest('[role="dialog"]')) return;

				const isCell = getCell(activeElement) === activeElement;
				const index = activeElement.getAttribute('tabindex');
				if (isCell) activeElement.setAttribute('tabindex', '0');
				const focusable = getFocusableElements();
				const currentFocusableElementsCell = getCell(activeElement);
				const nextFocusableElementsCell = getCell(focusable[focusable.indexOf(activeElement) + 1]);
				if (isCell) activeElement.setAttribute('tabindex', index ?? '-1');

				if (
					currentFocusableElementsCell === nextFocusableElementsCell ||
					!nextFocusableElementsCell
				)
					return;
				const nextFocusableElementOutsideTable = focusable
					.slice(focusable.indexOf(activeElement))
					.find((ele): ele is HTMLElement => !table.contains(ele));

				if (!nextFocusableElementOutsideTable) {
					const lastInsideTable = focusable.slice(focusable.indexOf(activeElement)).at(-1);
					if (!(lastInsideTable instanceof HTMLElement)) return;
					lastInsideTable.focus({ preventScroll: true });
					return;
				}
				event.preventDefault();
				nextFocusableElementOutsideTable?.focus();
				return;
			}
			if (!activeElement || !(activeElement instanceof HTMLElement)) return;
			if (!currentTr || !(currentTr instanceof HTMLTableRowElement)) return;
			const currentTd = getCell(activeElement);
			if (!currentTd) return;

			const currentTds = Array.from(currentTr.children).filter((n) => {
				return n.getAttribute('aria-hidden') !== 'true';
			});
			const currentTdIndex = currentTds.indexOf(currentTd);
			const rows = Array.from(table.querySelectorAll('tr:not([aria-hidden="true"])'));
			const currentTrIndex = rows.indexOf(currentTr);

			const isKeyboardNavigableElement =
				activeElement instanceof HTMLTableCellElement ||
				(activeElement instanceof HTMLButtonElement &&
					activeElement.getAttribute('aria-hidden') === 'false');
			if (!isKeyboardNavigableElement) return;
			const next = (() => {
				if (event.key === 'ArrowUp' || event.key === 'j') {
					/* same index, in next row or first index in next row [grouped] */
					const children = Array.from(rows[currentTrIndex - 1]?.children ?? []).filter((ele) => {
						return ele.getAttribute('aria-hidden') !== 'true';
					});
					return children[currentTdIndex] ?? rows[currentTrIndex - 1].children[0];
				}
				if (event.key === 'ArrowDown' || event.key === 'k') {
					/* same index, in next row or first index in next row [grouped] */
					const children = Array.from(rows[currentTrIndex + 1]?.children ?? []).filter((ele) => {
						return ele.getAttribute('aria-hidden') !== 'true';
					});
					return children[currentTdIndex] ?? rows[currentTrIndex + 1].children[0];
				}
				if (event.key === 'ArrowLeft') return currentTds[currentTdIndex - 1];
				if (event.key === 'ArrowRight') return currentTds[currentTdIndex + 1];
				if (event.key === 'End') return currentTds[currentTds.length - 1];
				if (event.key === 'Home') return currentTds[0];
				if (event.key === 'Enter') return currentTd.click();
			})();

			if (!(next instanceof HTMLElement)) return;
			event.stopImmediatePropagation(); // Stop meatball menu onKeyDown hijacking focus
			event.preventDefault();
			const button = next.querySelector('button[aria-hidden="false"]');
			if (button && button instanceof HTMLElement) return button.focus();
			return next.focus();
		};
		const onFocusIn = (e: FocusEvent) => {
			/* Focus first element inside table, if we've referred focus from outside table (shift+tab back into table) */
			const target = e.target;
			if (
				!(target instanceof HTMLElement) ||
				!(e.relatedTarget instanceof Element) ||
				table.contains(e.relatedTarget)
			) {
				if (target instanceof HTMLElement) lastFocusedElement.current = getCell(target);
				return;
			}
			const focusable = getFocusableElements();
			const indexOfTable = focusable.findIndex((ele) => table.contains(ele));
			const indexOfRelatedTarget = focusable.indexOf(e.relatedTarget);
			if (indexOfRelatedTarget < indexOfTable) {
				lastFocusedElement.current = undefined;
			}
		};

		const onKeyDownWindow = (e: KeyboardEvent) => {
			/*
				If focus is on the first element outside the table.
				prevent the default behaviour and move focus either to the last focuse element inside the table, or the first table cell.
			*/
			if (e.key === 'Tab' && e.shiftKey) {
				const focusable = getFocusableElements();
				const elementOutsideTable = focusable
					.slice(focusable.findIndex((ele) => table.contains(ele)))
					.find((ele) => !table.contains(ele));
				if (elementOutsideTable === e.target) {
					e.preventDefault();
					if (lastFocusedElement.current && table.contains(lastFocusedElement.current)) {
						return lastFocusedElement.current.focus();
					}
					focusable.find((ele): ele is HTMLElement => table.contains(ele))?.focus();
				}
			}
		};
		table.addEventListener('keydown', onKeyDown);
		table.addEventListener('focusin', onFocusIn);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('keydown', onKeyDownWindow);
		return () => {
			table.removeEventListener('keydown', onKeyDown);
			table.removeEventListener('focusin', onFocusIn);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('keydown', onKeyDownWindow);
		};
		// RefObject is a constant reference.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ref.current]);
};
